import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, Building, Wind, Sun, Eye, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ROUTE_PATHS } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

const facadeSystems = [
  {
    id: 'curtain-wall-advanced',
    title: 'Advanced Technology Curtain Wall Systems',
    description: 'High-performance, energy-efficient and aesthetic curtain wall solutions.',
    image: IMAGES.CEPHE_GIYDIRME_286,
    features: [
      'Structural glazing technology',
      'Thermal break system for insulation',
      'Earthquake resistance and flexibility',
      'Fire safety class EI60',
      'Sound insulation Rw 45 dB',
      'Easy maintenance and long life'
    ],
    applications: [
      'Skyscrapers and high-rise buildings',
      'Commercial and office complexes',
      'Hospitals and healthcare facilities',
      'Airports and transportation centers'
    ],
    specifications: [
      'Glass thickness: 6-24mm',
      'Wind load: 3.0 kN/m²',
      'Air permeability: A4',
      'Water permeability: E1800'
    ]
  },
  {
    id: 'window-door-systems',
    title: 'High Performance Window and Door Systems',
    description: 'Energy efficiency and comfort focused window and door solutions.',
    image: IMAGES.DO_RAMA_112_307,
    features: [
      'Superior thermal insulation (Uf ≤ 1.0 W/m²K)',
      'Multi-chamber profile structure',
      'Triple glazing options',
      'Automatic locking systems',
      'Anti-theft security',
      'Various color and texture options'
    ],
    applications: [
      'Luxury residential projects',
      'Commercial buildings',
      'Educational institutions',
      'Healthcare facilities'
    ],
    specifications: [
      'Profile depth: 70-85mm',
      'Thermal transmittance: Uf ≤ 1.0 W/m²K',
      'Air permeability: Class 4',
      'Water tightness: Class E1800'
    ]
  },
  {
    id: 'composite-panels',
    title: 'Aluminum Composite Panel Systems',
    description: 'Lightweight, durable and aesthetic facade cladding solutions.',
    image: IMAGES.COMPOSITE_308,
    features: [
      'Lightweight structure (4-6 kg/m²)',
      'High flatness and stability',
      'Fire resistance A2-s1,d0',
      'UV resistance and color stability',
      'Easy workability',
      'Wide range of colors and patterns'
    ],
    applications: [
      'Modern office buildings',
      'Shopping centers',
      'Industrial facilities',
      'Sports and cultural centers'
    ],
    specifications: [
      'Panel thickness: 3-6mm',
      'Dimensions: 1220x2440mm - 1500x4000mm',
      'Core: PE/FR core',
      'Surface: PVDF/Polyester coating'
    ]
  },
  {
    id: 'louvre-profiles',
    title: 'Louvre Profiles',
    description: 'Architectural louvre (sun-shading blade) profile systems that combine solar control, natural ventilation and aesthetic facade design.',
    image: IMAGES.EKRAN_GORUNTUSU_LOUVRE,
    features: [
      'Fixed and adjustable blade options',
      'High-strength 6000 series alloy',
      'Powder-coated or anodized surface',
      'Custom blade width and depth',
      'Motor-driven automated systems',
      'High wind-load resistance'
    ],
    applications: [
      'Office and commercial building facades',
      'Car parks and ventilated structures',
      'Sunshade and solar screening',
      'Decorative architectural cladding'
    ],
    specifications: [
      'Blade width: 100–300mm',
      'Alloy: 6063-T5 / 6061-T6',
      'Surface: Powder coat / Anodize',
      'Wind resistance: up to 3.0 kN/m²'
    ]
  }
];

export default function FacadeSystemsEn() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.ALUMINUM_PRODUCTS_1} 
            alt="Facade Systems" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/60" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <Button variant="outline" asChild className="mb-6 bg-white/10 border-white/20 text-white hover:bg-white/20">
              <Link to={ROUTE_PATHS.PRODUCTS_EN}>
                <ArrowLeft className="mr-2 w-4 h-4" />
                Back to Products
              </Link>
            </Button>
            
            <h1 className="text-5xl font-bold text-white mb-6">
              Facade & Joinery Systems
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Comprehensive facade and joinery solutions combining aesthetic design with superior performance for modern architecture.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="space-y-24"
          >
            {facadeSystems.map((system, index) => (
              <motion.div
                key={system.id}
                variants={staggerItem}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 !== 0 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 !== 0 ? 'lg:col-start-2' : ''}>
                  <div className="relative rounded-2xl overflow-hidden group">
                    <img 
                      src={system.image} 
                      alt={system.title}
                      className={`w-full ${system.id === 'composite-panels' ? 'h-[500px] object-cover object-center' : 'h-[400px] object-cover'} group-hover:scale-105 transition-transform duration-500`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                </div>
                
                <div className={index % 2 !== 0 ? 'lg:col-start-1' : ''}>
                  <motion.div variants={fadeInUp} className="space-y-6">
                    <div>
                      <h2 className="text-3xl font-bold text-primary mb-4">
                        {system.title}
                      </h2>
                      <p className="text-lg text-muted-foreground leading-relaxed">
                        {system.description}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Shield className="w-5 h-5 mr-2 text-primary" />
                        Key Features
                      </h3>
                      <ul className="grid grid-cols-1 gap-2">
                        {system.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0" />
                            <span className="text-foreground">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Building className="w-5 h-5 mr-2 text-primary" />
                        Applications
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {system.applications.map((app, appIndex) => (
                          <span 
                            key={appIndex}
                            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
                          >
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold mb-4 flex items-center">
                        <Layers className="w-5 h-5 mr-2 text-primary" />
                        Technical Specifications
                      </h3>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {system.specifications.map((spec, specIndex) => (
                          <li key={specIndex} className="flex items-center">
                            <Eye className="w-4 h-4 mr-2 text-primary" />
                            <span className="text-sm text-muted-foreground">{spec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl font-bold mb-6">
              Ready to Transform Your Building?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Contact us for customized facade and joinery solutions that meet your specific project requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8 py-3">
                <Link to={ROUTE_PATHS.CONTACT_EN}>
                  Request Quote
                </Link>
              </Button>
              <Button variant="outline" asChild size="lg" className="text-lg px-8 py-3">
                <a href="/catalogue.pdf" target="_blank" rel="noopener noreferrer">
                  Download Catalog
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}